// StreamCard.js
import React from 'react';
import { PlayFill } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';

import FeedItem from '../feed/FeedItem';
import { openExternal } from '../../utils/external';
import {
  formatStreamTime,
  governmentName,
  scopeLabel,
  streamDate,
  streamWatchUrl,
  thumbnailFor,
} from '../../utils/streams';

import './Streams.css';

/**
 * StreamCard
 *
 * One stream on the Live streams page, rendered by FeedItem itself rather than
 * a look-alike, so it is the same card as every other item in the feed and
 * follows any change made there. The stream maps onto a feed item like this:
 *
 *   media           the video's thumbnail with a play button, where the caller
 *                   asks for it; tapping it plays the stream
 *   source header   the government ("Cerritos city"), as a Recents item names
 *                   its collection; tapping it opens the official page that
 *                   links the channel
 *   title           the stream's title; tapping it plays the stream in the app
 *   date row        the day it went live, is scheduled, or ended
 *   detail line     scope and status: "City · Live now · live for 260 min · 30
 *                   watching", "Scheduled · Starts Tue 3:00 PM",
 *                   "Recording · Ended 2:10 PM"
 *
 * No footer buttons. Save, Share and Watch on YouTube are all in the player
 * (StreamPlayerModal), which the thumbnail and the title open, so the card
 * stays a picture and a few lines.
 *
 * Live cards carry an accent ring and a pulsing accent dot (Streams.css).
 */
function StreamCard({ stream, kind = 'live', showThumbnail = false, onWatch }) {
  const { t } = useTranslation('common');

  const isLive = kind === 'live';
  const watch = () => onWatch && onWatch(stream);
  const watchUrl = streamWatchUrl(stream);
  const officialPage = stream.channel?.linked_from?.page || null;

  // The status line: scope first, as the source header's kind, then what the
  // stream is doing.
  let status;
  let detail = null;
  if (isLive) {
    status = t('common:liveNowLabel', 'Live now');
    detail = stream.continuous
      ? t('common:streamContinuous', 'Continuous stream')
      : [
          typeof stream.live_minutes === 'number'
            ? t('common:liveForMinutes', 'live for {{count}} min', { count: stream.live_minutes })
            : null,
          typeof stream.viewers === 'number'
            ? t('common:liveViewers', '{{count}} watching', { count: stream.viewers.toLocaleString() })
            : null,
        ]
          .filter(Boolean)
          .join(' \u00b7 ');
  } else if (kind === 'upcoming') {
    status = t('common:streamScheduled', 'Scheduled');
    const when = formatStreamTime(stream.scheduled_start);
    // A scheduled time is the government's plan; the guide asks that a stream
    // past it be said to be running late rather than shown as if on time.
    detail = stream.start_time_passed
      ? t('common:streamRunningLate', 'Was due {{time}}, not started yet', { time: when })
      : when && t('common:streamStarts', 'Starts {{time}}', { time: when });
  } else {
    status = t('common:streamRecording', 'Recording');
    const ended = formatStreamTime(stream.actual_end);
    detail = ended && t('common:streamEnded', 'Ended {{time}}', { time: ended });
  }

  const statusText = [scopeLabel(stream.scope, t), status, detail].filter(Boolean).join(' \u00b7 ');

  const statusLine = (
    <span className={`stream-status-line ${isLive ? 'stream-status-line--live' : ''}`}>
      {isLive && <span className="stream-dot" aria-hidden />}
      {statusText}
    </span>
  );

  const thumbnail = showThumbnail ? thumbnailFor(stream) : null;
  const media = thumbnail ? (
    <button
      type="button"
      className="stream-thumb"
      onClick={watch}
      aria-label={t('common:watchStream', 'Watch {{title}}', { title: stream.title })}
    >
      <img src={thumbnail} alt="" loading="lazy" />
      <span className="stream-thumb-play" aria-hidden>
        <PlayFill size={26} />
      </span>
    </button>
  ) : null;

  const item = {
    id: stream.video_id,
    title: stream.title,
    url: watchUrl,
    document_link: watchUrl,
    created_at: streamDate(stream),
  };

  return (
    <FeedItem
      item={item}
      // feedName/branch/topic stay English, per the stored-data convention.
      feedName={governmentName(stream) || 'Government stream'}
      branch="Live Streams"
      topic={stream.scope || 'N/A'}
      variant="default"
      media={media}
      isRecentsItem
      onSourceClick={officialPage ? () => openExternal(officialPage) : () => {}}
      onTitleClick={watch}
      details={[statusLine]}
      shareUrl={watchUrl}
      className={`feed-item--stream ${isLive ? 'feed-item--live' : ''}`}
      hideFooter
    />
  );
}

export default StreamCard;